/**
 * Canonical Transform - Phase 1
 * Adapted for BillPortal-UI: flattens common fields and identifiers/charges
 */

import { CAT, type CatKey } from '@/utils/canonical/CAT';
import { unwrapApiPayload } from '@/utils/canonical/unwrapApiPayload';

const BASIC_KEYS = new Set([
  // Common basic fields (snake_case normalized)
  'supplier_name',
  'account_number',
  'invoice_date',
  'invoice_number',
  'due_date',
  'billing_period',
  // Alternate patterns
  'billing_period_from','billing_period_to',
]);
// Strict financial keys to avoid accidental matches (e.g., due_date)
const FINANCIAL_KEY_SET = new Set([
  'current_bill_amount',
  'total_amount_due',
  'previous_bill_amount',
  'previous_bill_payment',
  'opening_balance',
  'late_payment_fee',
  'adjustments',
  'gst',
  'solar_rebate'
]);
const ADDRESS_KEYS   = /^(service_address|address|city|state|postcode|post_code|country)$/i;
const USAGE_KEYS     = /^(usage|consumption|rate)$/i;
const TECHNICAL_KEY_SET = new Set(['file_name','file_extension','template_name','template_version','parsed_date']);

const isRenderable = (v: unknown) => {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (Array.isArray(v)) return false;
  if (typeof v === 'object') return false;
  return true; // keep 0, negatives, booleans
};

// Normalize keys to snake_case-ish for matching (handles camelCase inputs)
const normalizeKey = (k: string) => k
  .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
  .replace(/\s+/g, '_')
  .toLowerCase();

const getByKey = (obj: any, key: string) => {
  if (!obj) return undefined;
  if (Object.prototype.hasOwnProperty.call(obj, key)) return obj[key];
  const target = normalizeKey(key);
  for (const k of Object.keys(obj)) {
    if (normalizeKey(k) === target) return (obj as any)[k];
  }
  return undefined;
};

export interface CanonicalResult {
  categories: Record<string, Array<{label: string; value: any; keyPath: string}>>;
  meters: any[];
  identifiers: any[];
  lineItems: any[];
  totalFields: number;
}

export function canonicalTransform(apiResponse: any): CanonicalResult {
  const results = unwrapApiPayload(apiResponse);

  const cats: Record<string, {label:string; value:any; keyPath:string}[]> = {};
  const taken = new Set<string>();
  const put = (cat: CatKey, labelKey: string, value: any, keyPath: string) => {
    if (!isRenderable(value) || taken.has(keyPath)) return;
    const label = labelKey.replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase());
    const bucket = CAT[cat];
    (cats[bucket] ??= []).push({ label, value, keyPath });
    taken.add(keyPath);
  };

  // 1) Top-level snake_case primitives
  Object.entries(results || {}).forEach(([k, v]) => {
    const kl = normalizeKey(k);
    if (kl === 'raw_payload' || Array.isArray(v) || (typeof v === 'object' && v)) return;
    if (BASIC_KEYS.has(kl))      return put('Basic', k, v, `top.${k}`);
    if (FINANCIAL_KEY_SET.has(kl)) return put('Financial', k, v, `top.${k}`);
    if (ADDRESS_KEYS.test(kl))   return put('Address', k, v, `top.${k}`);
    if (USAGE_KEYS.test(kl))     return put('Usage', k, v, `top.${k}`);
    if (TECHNICAL_KEY_SET.has(kl)) return put('Technical', k, v, `top.${k}`);
    // Explicitly keep these in Other as per requirement
    if (kl === 'currency' || kl === 'is_final_bill' || kl === 'is_ocr_bill') return put('Other', k, v, `top.${k}`);
    return put('Other', k, v, `top.${k}`);
  });

  // 2) identifiers -> meters + line items (if provided)
  const idents = getByKey(results, 'parserIdentifiersData')
    ?? getByKey((results as any)?.raw_payload, 'parserIdentifiersData')
    ?? getByKey((results as any)?.data, 'parserIdentifiersData')
    ?? [];
  idents.forEach((ident: any, i: number) => {
    ([
      'identifier','utilityType','subAccount','serviceTotal',
      'siteAddress','siteCity','siteState','siteCountry','sitePostCode',
      'periodFrom','periodTo','usageMonth','serviceUsage','readType'
    ] as const).forEach(f => {
      const v = getByKey(ident, f);
      if (!isRenderable(v)) return;
      const cat: CatKey =
        (['siteAddress','siteCity','siteState','siteCountry','sitePostCode'] as any).includes(f)
          ? 'Address'
          : ((['periodFrom','periodTo','usageMonth','serviceUsage','readType'] as any).includes(f) ? 'Usage' : 'Identifier');
      put(cat, f, v, `ident[${i}].${f}`);
    });

    const charges = getByKey(ident, 'parserChargesData') ?? [];
    charges.forEach((ch: any, c: number) => {
      ([
        'meterNumber','meterMultiplier','previousReading','currentReading',
        'quantity','quantityUOM','periodFrom','periodTo','readType'
      ] as const).forEach(f => {
        const v = getByKey(ch, f);
        if (isRenderable(v)) put('Meter', f, v, `ident[${i}].ch[${c}].${f}`);
      });

      ([
        ['lineItemDescription','desc'],
        ['rate','rate'],
        ['rateUOM','rateUOM'],
        ['total','total'],
        ['quantity','qty'],
        ['quantityUOM','qtyUOM'],
      ] as const).forEach(([label, short]) => {
        const v = getByKey(ch, label);
        if (isRenderable(v)) put('LineItems', label, v, `ident[${i}].ch[${c}].${short}`);
      });
    });
  });

  return {
    categories: cats,
    meters: [],
    identifiers: [],
    lineItems: [],
    totalFields: Object.values(cats).reduce((sum, cat) => sum + cat.length, 0)
  };
}
